import {useNavigate, useSearchParams} from "react-router-dom";
import {resolveTab} from "src/utils/tab.util";
import {useTranslation} from "react-i18next";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import SettingsEntityHeader from "../../../layout/SettingsEntityHeader";
import DetailsLoading from "crqlar-react-common/dist/components/DetailsLoading";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import EmptyState from "crqlar-react-common/dist/components/EmptyState";
import {MewsService} from "crqlar-react-common/dist/services/integrations/mews/mews.service";
import {Box, Divider, Stack, Tab, Tabs} from "@mui/material";
import Typography from "@mui/material/Typography";
import {MewsConfig} from "crqlar-react-common/dist/models/integrations/mews/mews.config";
import {SectorService} from "crqlar-react-common/dist/services/sector/sector.service";
import {SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import MewsConfigForm from "./MewsConfigForm";
import MewsTasks from "./MewsTasks";
import Button from "@mui/material/Button";
import ElectricalServicesIcon from "@mui/icons-material/ElectricalServices";


const MewsConfigDetails = () => {

    useTrackScreenVisit('system mews config details')

    //region State

    const {t} = useTranslation()
    const navigate = useNavigate()

    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = resolveTab(searchParams.get('tab'), ['config', 'tasks'], 'config');
    const setActiveTab = (newTab: string) => {
        setSearchParams({tab: newTab});
    };

    const mewsConfigData = useEntity<MewsConfig>({
        get: async () => {
            return await MewsService.find()
        },
        create: MewsService.create,
        update: MewsService.update,
        delete: (id) => MewsService.delete({id: [id]}),
        isValid: (mewsConfig) => {
            return !!mewsConfig.name
        },
        cleanupBeforeSave: (mewsConfig) => {
            return {
                ...mewsConfig,
                name: mewsConfig.name?.trim()
            }
        }
    })

    const mewsConfig = mewsConfigData.item

    const handleCreate = async () => {
        const sectors = await SectorService.getAllSectors({limit: {offset: 0, limit: 1000}})

        mewsConfigData.startCreate({
            pollerConfig: {
                fullSync: {
                    profileDaysPast: 365,
                    reservationDaysPast: 365,
                    reservationDaysFuture: 365,
                    staleTimeoutMinutes: 5 * 60,
                },
                quickSync: {
                    staleTimeoutMinutes: 10,
                    syncPeriodMinutes: 5
                }
            },
            dataProcessingRules: {
                accommodationSectorId: sectors.content?.find(s => s.sectorType === SectorType.ACCOMMODATION)?.id,
                allowCharge: false
            }
        } as MewsConfig)
    }

    //endregion State

    const headerSection = (
        <SettingsEntityHeader
            title={mewsConfig.name}
            data={mewsConfigData}
            back={() => navigate('/system')}
        />
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete integration')}
                description={t('This permanently deletes the integration configuration and stops its syncing immediately. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={mewsConfigData.saving}
                onDelete={() => mewsConfigData.removeNow()}
                reloadOnDelete
            />

            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {mewsConfig.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    const content = (
        <>
            {headerSection}

            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    mx: 'auto',
                    mt: 3,
                    gap: 6,
                }}
            >
                {
                    mewsConfigData.isMissing &&
                    <EmptyState
                        icon={<ElectricalServicesIcon/>}
                        title={t('No integration configured')}
                        description={t('Create the configuration to connect this integration.')}
                    >
                        <Button variant={'contained'} onClick={handleCreate}>
                            {t('Create')}
                        </Button>
                    </EmptyState>
                }

                {
                    !!mewsConfig.id &&
                    <Tabs
                        value={activeTab}
                        onChange={(e, v) => setActiveTab(v)}
                        aria-label="mews tabs"
                        sx={{
                            borderBottom: 1,
                            borderColor: 'divider',
                        }}
                    >
                        <Tab label={t('Configuration')} value={'config'}/>
                        <Tab label={t('Tasks')} value={'tasks'}/>
                    </Tabs>
                }

                {
                    !mewsConfigData.isMissing && activeTab === 'config' && (
                        <>
                            <MewsConfigForm mewsConfigData={mewsConfigData}/>

                            {
                                !!mewsConfig.id &&
                                <>
                                    <Divider sx={{mt: 4}}/>
                                    {dangerZoneSection}
                                </>
                            }
                        </>
                    )
                }

                {
                    activeTab === 'tasks' && (
                        <MewsTasks configData={mewsConfigData}/>
                    )
                }
            </Stack>
        </>
    )

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {mewsConfigData.loading ? <>{headerSection}<DetailsLoading/></> : content}
        </Box>
    );
}

export default MewsConfigDetails;
